import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion'; 
import { Menu, X, Shield, Lock, Zap, MessageSquare } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [clickCount, setClickCount] = useState(0); 
  const [activeSection, setActiveSection] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  // --- SCROLL SPY LOGIC ---
  useEffect(() => {
    if (!isHomePage) return;
    const sections = ['about', 'facilities', 'achievements', 'programs', 'contact'];
    const observerOptions = { root: null, rootMargin: '-40% 0px -40% 0px', threshold: 0 };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    }, observerOptions);
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isHomePage]);

  // CRITICAL FIX: Smooth scrolling for mobile and desktop
  const handleNavClick = (e, id) => {
    setIsOpen(false); // Close menu immediately
    
    if (!isHomePage) {
      navigate(`/#${id}`);
      return;
    }
    
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Adjust for navbar height
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      window.history.pushState(null, '', `/#${id}`);
    }
  };

  const handleSecretAccess = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    if (newCount >= 3) {
      navigate('/admin/login');
      setClickCount(0);
    }
  };

  const navLinks = [
    { id: 'about', name: 'About' },
    { id: 'facilities', name: 'Facilities' },
    { id: 'achievements', name: 'Achievements' },
    { id: 'programs', name: 'Programs' },
    { id: 'contact', name: 'Contact' },
  ];

  return (
    <nav className="fixed w-full z-[9999] bg-black/90 backdrop-blur-xl border-b border-white/5 h-24">
      <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
        
        {/* LOGO AREA */}
        <div onClick={handleSecretAccess} className="cursor-pointer z-[10000]">
          <Link to="/" className="flex items-center gap-4 group">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden border-2 border-[#FFD700] shadow-[0_0_20px_rgba(255,215,0,0.15)] group-hover:scale-105 transition-all duration-500 flex items-center justify-center bg-black">
              <img 
                src="/images/logo.png" 
                alt="Chaitanya Shooting Academy Logo" 
                className="w-[85%] h-[85%] object-contain"
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="hidden sm:block text-left">
              <h2 className="font-heading text-xl font-bold tracking-tighter text-white uppercase leading-none">
                Chaitanya <span className="text-[#FFD700]">Shooting</span>
              </h2>
              <p className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 font-bold">Mission Olympic</p>
            </div>
          </Link>
        </div>

        {/* DESKTOP NAVIGATION */}
        <div className="hidden lg:flex items-center gap-10">
          <div className="flex items-center gap-8 text-[11px] font-bold uppercase tracking-widest">
            {navLinks.map((link) => (
              <a 
                key={link.id}
                href={`/#${link.id}`} 
                onClick={(e) => handleNavClick(e, link.id)} 
                className={`transition-all duration-300 relative py-2 ${activeSection === link.id ? 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'text-zinc-400 hover:text-white'}`}
              >
                {link.name}
                {activeSection === link.id && (
                  <motion.div layoutId="activeGlow" className="absolute -bottom-1 left-0 w-full h-[2px] bg-[#B91C1C] shadow-[0_0_10px_#B91C1C]" />
                )}
              </a>
            ))}
            
            <Link to="/admin/login" className="text-zinc-400 hover:text-[#B91C1C] transition-all flex items-center gap-2">
              <Lock size={12} /> Admin Access
            </Link>
          </div>
          <Link to="/book-demo" className="bg-[#B91C1C] text-white px-10 py-3.5 rounded-full text-[11px] font-black uppercase tracking-widest hover:bg-white hover:text-black transition-all shadow-lg active:scale-95">
            Schedule Trial
          </Link>
        </div>

        {/* MOBILE TOGGLE */}
        <div className="flex items-center gap-4 lg:hidden z-[10000]">
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="text-white p-2 outline-none"
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            {isOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>
      </div>

      {/* MOBILE DROPDOWN MENU */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            id="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-24 left-0 w-full h-screen bg-black z-[9998] flex flex-col p-8 gap-8 lg:hidden shadow-2xl overflow-y-auto pb-40"
            role="navigation"
            aria-label="Mobile navigation menu"
          >
            {navLinks.map((link) => (
              <button 
                key={link.id}
                onClick={(e) => handleNavClick(e, link.id)} 
                className={`text-left text-4xl font-heading italic uppercase tracking-tighter transition-all ${
                  activeSection === link.id ? 'text-white border-l-4 border-[#B91C1C] pl-4' : 'text-zinc-600'
                }`}
              >
                {link.name}
              </button>
            ))}
            
            <div className="mt-4 pt-8 border-t border-white/5 space-y-6">
              <Link to="/admin/login" onClick={() => setIsOpen(false)} className="text-2xl font-heading text-zinc-500 italic uppercase flex items-center gap-4">
                <Shield size={24} /> Admin Access
              </Link>
              
              <Link to="/book-demo" onClick={() => setIsOpen(false)} className="bg-[#B91C1C] text-white py-6 rounded-2xl text-center font-black uppercase tracking-widest text-base shadow-xl block w-full">
                Schedule Trial Session
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}